<template>
	<div class="theme-editor">
		<h2>Theme Editor</h2>
		<v-divider></v-divider>
		<h4 class="mt-7">Add Logo</h4>
		<LogoPicker v-model="logo" />
		<h4 class="mt-7">Theme Colors</h4>
		<ColorsPicker v-model="colors" :pallet="palletColors" />
		<div class="d-flex justify-center">
			<v-switch v-model="isDark" :label="isDark ? 'Dark Theme' : 'Light Theme'" />
		</div>
		
		<div class="mt-7">
			<v-btn @click="testDriveTheme()" color="secondary" depressed title="Test your styles">Test Drive</v-btn>
			<v-btn v-if="beforeTestDriveState" @click="undoTestDrive()" class="ml-2" title="Undo test-drive">Undo Test Drive</v-btn>
			<p class="pt-3 body-2">*Test Drive will apply your color choices without saving them</p>
		</div>

		<div class="mx-n6 mb-4">
			<v-theme-provider :dark="isDark" :light="!isDark">
				<v-card :elevation="0" class="px-5">
					<Preview :colors="compiledColors" class="pt-4" />
				</v-card>
			</v-theme-provider>
		</div>
		<v-divider></v-divider>
		<div class="d-flex mt-7">
			<v-btn depressed @click="reset()">Reset</v-btn>
			<v-spacer />
			<v-btn @click="save()" depressed color="primary">Save</v-btn>
		</div>
	</div>
</template>

<script>
import LogoPicker from "./components/logo-picker.vue";
import ColorsPicker from "./components/colors-picker.vue";
import Preview from "./components/preview.vue";
import QuilityAPI from '@/store/API/QuilityAPI.js';
import Tenant from '@/store/Models/Tenant'
import { initThemeFromColor, getTextColor, createColorPallet } from '@/plugins/tenant-theme'

const deepCopy = (obj) => JSON.parse(JSON.stringify(obj))

const palletColors = ['primary', 'secondary']

export default {
	components: {
		LogoPicker,
		ColorsPicker,
		Preview,
	},

	data() {
		return {
			palletColors,
			isDark: false,
			colors: initThemeFromColor('#3f51b5', false).colors,
			logo: "",
			initialState: null,
			beforeTestDriveState: null,
			origLogo : ""
		};
	},

	mounted(){
		this.logo = this.current_tenant?.TenantConfig.features.TenantLogo;
	},

	computed: {
		fullTheme() {
			const textColors = Object.keys(this.colors).reduce((acc, color) => {
				if (['primary', 'secondary'].some((c) => color.startsWith(c))) {
					acc[`on-${color}`] = getTextColor(this.colors[color])
				}
				return acc
			}, {})

			return {
				...this.colors,
				...textColors,
			}
		},
		compiledColors() {
			return Object.keys(this.fullTheme).map((key, i, arr) => {
				return {
					name: key.toLowerCase().replaceAll(/ /g, "-"),
					value: this.fullTheme[key],
				};
			});
		},
	},

	methods: {
		applyTheme() {
			this.$setTenantTheme({ colors: this.fullTheme, dark: this.isDark });
		},
		testDriveTheme() {
			if (this.beforeTestDriveState === null) {
				this.beforeTestDriveState = this.$getTenantTheme()
			}
			this.origLogo = this.current_tenant.TenantConfig.features.TenantLogo;
			if (this.logo != null){
                this.current_tenant.TenantConfig.features.TenantLogo = this.logo
            }
			this.applyTheme()
		},
		undoTestDrive() {
			this.$setTenantTheme(this.beforeTestDriveState);
			this.current_tenant.TenantConfig.features.TenantLogo = this.origLogo
			this.beforeTestDriveState = null
		},
		async save() {
			this.current_tenant.TenantConfig.features.Theme = { colors: this.fullTheme, dark: this.isDark };

			const response = await QuilityAPI.saveTheme(this.current_tenant.ID, this.current_tenant, this.logo)
			if (response.success !== true) {
				console.error(response.msg)
			} else {
				Tenant.insert({ data: response.data });
			}

			this.applyTheme()
		},
		reset() {
			this.colors = this.initialState.colors
			this.isDark = this.initialState.dark
			this.logo = this.initialState.logo
			this.applyTheme()
		},
		updateColorsFromTenant(theme) {
			this.colors = Object
				.keys(this.colors)
				// Remove on- colors, so user unable to change them
				.filter((key) => !key.startsWith('on'))
				.reduce((acc, key) => {
					acc[key] = theme.colors[key] || this.colors[key];
					return acc
				}, {});
			this.isDark = theme.dark;
		},
		makePallet(colorName, value) {
			const pallet = createColorPallet(colorName, value)

			Object.keys(pallet).forEach((key) => {
				this.colors[key] = pallet[key]
			})
		}
	},

	watch: {
		colors: {
			handler(newValue, oldValue) {
				palletColors
					.forEach((key) => {
						if (newValue?.[key] === oldValue?.[key] || !newValue?.[key]) { return }

						this.makePallet(key, newValue[key]);
					})
			},
			immediate: true,
		},
		$tenantTheme: {
			handler(theme) {
				this.initialState = deepCopy(this.$data)
				this.updateColorsFromTenant(theme)
			},
			immediate: true,
			deep: true,
		}
	}
};
</script>

<style lang="scss" scoped>
h5 {
	margin-bottom: 2rem;
	font: normal normal normal 18px/22px Montserrat !important;
}
</style>
